/** @module The catalogue picture of a card, or a box that names the card when there is none. */
import { useTranslation } from "react-i18next";

import { printedAs, useCard } from "@/lib/cards";
import { useImage } from "@/lib/images";
import { useNearScreen } from "@/lib/near-screen";

/**
 * Some sets have no picture at all (promos, galleries, trainer kits); a grey box would look
 * like it is still loading forever.
 */
export function CardFace({
  printId,
  name,
  quality = "low",
  className = "",
  lazy = false,
}: {
  printId: string;
  /** Alt text; without it the card id stands in. */
  name?: string;
  quality?: "low" | "high";
  className?: string;
  /** Load only once the picture scrolls near the screen. */
  lazy?: boolean;
}) {
  const { t } = useTranslation();
  const { ref, near } = useNearScreen(lazy);
  const { url, state } = useImage(near ? `/api/v1/images/cards/${printId}/${quality}` : null);
  // Only a card without picture needs its catalogue data, to name itself in the box.
  const card = useCard(state === "missing" ? printId : null);
  if (url) {
    return (
      <img
        src={url}
        alt={name ?? printId}
        className={`h-full w-full rounded-[var(--radius-sm)] object-contain ${className}`}
      />
    );
  }
  return (
    <div
      ref={ref}
      className={`flex h-full w-full flex-col items-center justify-center gap-1 rounded-[var(--radius-sm)] bg-(--surface-2) p-2 text-center ${className}`}
    >
      {state === "missing" ? (
        <>
          <span className="text-[length:var(--text-xs)] font-medium">
            {card.data?.name ?? printId}
          </span>
          <span className="tabular text-[length:var(--text-xs)] text-(--text-muted)">
            {printedAs(card.data, printId)}
          </span>
          <span className="text-[length:var(--text-xs)] text-(--text-faint)">
            {t("card.noPicture")}
          </span>
        </>
      ) : null}
    </div>
  );
}
